/**
 * Display an image or a video.
 *
 * TODO Consider using item templates
 * TODO Remove video.js if it is clear not to use it here.
 * @module
 * @import { VideoJsPlayer } from 'video.js';
 * @import { LightboxItem } from '../model/lightbox-item';
 */

export {NOVA_LIGHTBOX};

import {ElementLink, ElementSetup} from '@acng/frontend-stargazer';
import {Watch} from '@acng/frontend-relativity';
import {popup} from '@acng/frontend-discovery';
// bounty
import {allAnimationsFinished, loadImage} from '@acng/frontend-bounty';
import {create, DIV, IMG, VIDEO} from '@acng/frontend-bounty/dom/element.js';
import {isHTMLElement} from '@acng/frontend-bounty/dom/type.js';
import {CONTROLS, PLAYSINLINE, SRC} from '@acng/frontend-bounty/dom/attribute.js';
import {after, append, remove} from '@acng/frontend-bounty/dom/move.js';
import {add, all} from '@acng/frontend-bounty/style/element.js';
import {throttle} from '@acng/frontend-bounty/timing/throttle.js';

import {spinner} from '../../core/service/spinner.js';
import {player} from '../../core/factory/flowplayer.js';
import {getFallbackImageUrl} from '../service/fallback-image.js';
import {ctxLightboxItem} from '../context/lightbox-item.js';
import { user } from 'acng/user/user';

/**
 * Insert an element as sibling containing the picture
 * or video according to the {@link ctxLightboxItem}.
 *
 * @group DOM Element
 * @example
 * ```html
 * <nova-lightbox partner-lightbox></nova-lightbox>
 * ```
 */
const NOVA_LIGHTBOX = 'nova-lightbox';

ElementSetup(NOVA_LIGHTBOX, (name) => {
  const STYLE_PREFIX = 'lightbox-';
  const STYLE_BACKGROUND = 'background';
  const watch = Watch(name, ctxLightboxItem);

  ElementLink(name, (first) => {
    let last = first;

    /**
     * @type {VideoJsPlayer | undefined}
     */
    let vjs;

    /**
     * @type {HTMLImageElement | undefined}
     */
    let background;

    /**
     * @type {HTMLElement | undefined}
     */
    let content;

    /**
     * @param {HTMLElement} first
     * @param {LightboxItem | null} item
     */
    const update = async (first, item) => {
      if (!item) {
        return;
      }

      let previous = content;

      /**
       * @type {VideoJsPlayer | undefined}
       */
      let newVjs;

      const {type, src, format, thumbnail: fallbackSrc} = item;

      if (previous && isHTMLElement(previous, IMG)) {
        if (background) {
          remove(background);
        }

        add(previous, STYLE_PREFIX + STYLE_BACKGROUND);
        background = previous;
      } else if (!background && fallbackSrc) {
        background = create(IMG);

        add(background, STYLE_PREFIX + STYLE_BACKGROUND, ...all(first));
        background[SRC] = fallbackSrc;
        after(last, background);
        last = background;
      }

      if (type == VIDEO) {
        const video = create(VIDEO);

        /*
        content = create(DIV);
        append(content, video);
        newVjs = await player(video, [{type: 'video/mp4', src}], true);
        */
        //*
        video[SRC] = src;
        video[CONTROLS] = true;
        video[PLAYSINLINE] = true;
        content = video;
        await spinner(
          new Promise((resolve, reject) => {
            video.oncanplay = resolve;
            video.onerror = () => {
              popup(first).error(src);
              reject();
            };
            video.load();
          }),
          first
        );

        video.play();
        //*/
      } else {
        content = await spinner(loadImage(src, getFallbackImageUrl('none', format ?? '320')), first);
      }

      add(content, STYLE_PREFIX + type, ...all(first));
      after(last, content);
      last = content;
      await allAnimationsFinished(content);

      if (previous && previous != background) {
        remove(previous);
      }

      vjs?.dispose();
      vjs = newVjs;
    };

    return (element) => {
      watch(element, throttle(update));

      return () => vjs?.dispose();
    };
  });
});
